import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../contexts/BusinessContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { toast } from 'sonner';
import { CommandBarHints } from './commandbar/CommandBarParts';
import { CommandBarSearchInput, CommandBarResults } from './commandbar/CommandBarSections';
import { CommandBarAskPanel } from './commandbar/CommandBarAskPanel';
import { useAiDisclosure } from '../hooks/useAiDisclosure';
import { buildNavResults } from './commandbar/commandRegistry';
import { useAiAsk } from './commandbar/useAiAsk';
import { useCommandBarKeyboard } from './commandbar/useCommandBarKeyboard';
import { logger } from '../utils/logger';

/**
 * CommandBar — global ⌘K palette, AI-led.
 *
 * Single LLM agent (`POST /api/ai/ask`) handles every typed query:
 * lookup, aggregate, navigate, AND mutate. Static nav results stay
 * for instant matches like "go to inbox".
 *
 * Composition:
 *   - `commandRegistry`    — static nav/action result list
 *   - `useAiAsk`           — debounced agentic call
 *   - `CommandBarAskPanel` — renders answer + action chips + result rows
 *   - `CommandBarSections` — search input + (residual) nav results
 *
 * UX rules:
 *   - Static nav results render instantly for short queries (< 5 chars).
 *   - AI fires 700ms after the user stops typing on queries >= 5 chars.
 *   - When the AI returns `navigate`, we redirect automatically + close.
 *   - Clicking a result row deep-links to the item's home view.
 */
export default function CommandBar({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { businesses } = useBusiness();
  const { preferences } = usePreferences();

  const navResults = useMemo(() => buildNavResults(navigate), [navigate]);
  const disclosure = useAiDisclosure();
  const { answer, results: aiResults, actions, proposals, navigate: aiNavigate, loading, error, locked, steps, clear, confirmProposal, confirmAllProposals } = useAiAsk({ query, enabled: isOpen && disclosure.acknowledged });

  // If the agent decided this query is a navigation request, take the
  // user there immediately and close the palette. Effect runs only
  // when the navigate target actually changes — won't fire on every
  // re-render of the same response.
  useEffect(() => {
    if (!aiNavigate) return;
    try {
      navigate(aiNavigate);
    } catch (err) {
      logger.warn('AI navigate failed:', err?.message);
    }
    onClose();
  }, [aiNavigate, navigate, onClose]);

  // Reset state when the palette opens. `clear` is a stable useCallback,
  // so depending on it doesn't re-run the effect on every render.
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setResults(navResults.slice(0, 6));
      setSelectedIndex(0);
      clear();
    } else {
      // Closing the palette aborts any in-flight stream so we don't
      // leave an orphan backend generator running after the user
      // bailed (Esc / backdrop click) mid-response.
      clear();
    }
  }, [isOpen, navResults, clear]);

  // Filter local nav results as the user types
  useEffect(() => {
    if (!query.trim()) {
      setResults(navResults.slice(0, 6));
      return;
    }
    const filtered = navResults.filter((r) => r.title.toLowerCase().includes(query.toLowerCase()));
    setResults(filtered.slice(0, 8));
    setSelectedIndex(0);
  }, [query, navResults]);

  const onSelectAiResult = useCallback((item) => {
    if (!item?.path) {
      toast.info('No deep link for this item.');
      return;
    }
    navigate(item.path);
    onClose();
  }, [navigate, onClose]);

  // Keyboard nav still picks among the static nav results for
  // instant-match flows like "in‒inbox". The AI panel responds to
  // direct clicks (with mouse or arrow-key + tab in v2).
  const handleKeyDown = useCommandBarKeyboard({
    results,
    aiResult: null,
    selectedIndex,
    setSelectedIndex,
    executeAiResult: () => {},
    onClose,
  });

  if (!isOpen || !preferences.commandBarEnabled) return null;

  return (
    <div
      data-testid="commandbar-overlay"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] command-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-[min(92vw,980px)] rounded-[28px] overflow-hidden animate-fade-in pd-glass"
        style={{ boxShadow: 'var(--pd-shadow-3)' }}
      >
        <CommandBarSearchInput
          query={query}
          setQuery={setQuery}
          onKeyDown={handleKeyDown}
          onClose={onClose}
          inputRef={inputRef}
        />

        {/* DPDP: one-time AI disclosure — AI assist stays off until acknowledged. */}
        {!disclosure.acknowledged && query.trim().length >= 5 && (
          <div data-testid="ai-disclosure-gate" className="mx-4 mt-3 rounded-xl p-3 flex items-start gap-3" style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}>
            <div className="text-[12px] leading-relaxed flex-1" style={{ color: 'var(--pd-muted)' }}>
              <span className="font-semibold" style={{ color: 'var(--pd-text)' }}>Before AI assist runs: </span>
              {disclosure.text || 'AI features send your query and relevant workspace content to third-party LLM providers. Privileged businesses are excluded.'}
            </div>
            <button
              data-testid="ai-disclosure-ack-btn"
              onClick={disclosure.acknowledge}
              className="flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold"
              style={{ background: 'var(--accent, var(--pd-accent))', color: '#fff' }}
            >
              Got it — enable AI
            </button>
          </div>
        )}

        {/* Agentic AI panel — answer + actions + deep-linked result rows. */}
        <CommandBarAskPanel
          loading={loading}
          answer={answer}
          results={aiResults}
          actions={actions}
          proposals={proposals}
          error={error}
          locked={locked}
          steps={steps}
          onSelectResult={onSelectAiResult}
          onConfirmProposal={confirmProposal}
          onConfirmAll={confirmAllProposals}
        />

        <div className="grid lg:grid-cols-[1.2fr_0.8fr] min-h-[320px]">
          <CommandBarResults
            aiLoading={false}
            aiResult={null}
            results={results}
            selectedIndex={selectedIndex}
            query={query}
            onExecuteAi={() => {}}
            onClose={onClose}
          />
          <CommandBarHints onSuggest={setQuery} />
        </div>
      </div>
    </div>
  );
}
